import {UserState} from '../../../shared/UserState'
import {UIAjaxWrapper} from './network/UIAjaxWrapper'
import {UIAPIEndpoints} from './network/UIAPIEndpoints'
import {ArrayUtils, createDeferredPromise, delay} from '@colonist/utils'
import {UserStateData} from '../../../shared/UserStateData'
import {StorageWrapper} from './StorageWrapper'
import {AjaxDataTypes} from '../../../shared/AjaxDataTypes'

type UserStateChangeListener = (userState: UserState | undefined) => void

export class ClientUserState {
    private static _userState: UserState | undefined
    private static readonly changeListeners: UserStateChangeListener[] = []

    private static _stale: boolean = true // loaded from client storage, not server
    static isStale(): boolean {
        return this._stale
    }

    static get userState(): UserState | undefined {
        return this._userState
    }

    static readonly waitUntilFresh = createDeferredPromise()

    private static async _fetchUserState() {
        const userState = await this.fetchUserStateFromServer()
        if(userState == false) return false

        this._stale = false
        this.update(userState)
        this.waitUntilFresh.resolve()
        return true
    }

    private static async fetchUserStateFromServer(): Promise<UserStateData | undefined | false> {
        const getResponse = await UIAjaxWrapper.getRequestHandler(UIAPIEndpoints.apiUserState(), undefined)
        if(getResponse == undefined) return false
        const userStateGetResponse = getResponse.json as AjaxDataTypes.UserStateResponse

        UIAjaxWrapper.csrfToken = userStateGetResponse.csrfToken
        if(userStateGetResponse.userState != undefined) return userStateGetResponse.userState

        const postResponse = await UIAjaxWrapper.postRequestHandler(UIAPIEndpoints.apiUserStateCreate(), undefined, undefined)
        if(postResponse == undefined) return false
        const userStatePostResponse = postResponse.json as AjaxDataTypes.UserStateResponse

        return userStatePostResponse.userState
    }

    static async refresh() {
        await this._fetchUserState()
    }

    static update(userStateData: UserStateData | undefined) {
        if(userStateData != undefined) {
            StorageWrapper.setUserState(userStateData)
            this._userState = new UserState(userStateData)
        } else {
            StorageWrapper.clearUserState()
            this._userState = undefined
        }

        for(const changeListener of this.changeListeners) {
            changeListener(ClientUserState._userState)
        }
    }

    static registerChangeListener(callback: UserStateChangeListener): () => void {
        this.changeListeners.push(callback)
        callback(this._userState)

        return () => {
            ArrayUtils.removeElementFromArray(this.changeListeners, callback)
        }
    }

    static async init(): Promise<boolean> {
        if(this.loadInitialStateFromStorage()) {
            void this.initFetchLoop() // refresh user state in background
            return true
        }

        return this.initFetchLoop()
    }

    private static loadInitialStateFromStorage(): boolean {
        try {
            const cachedUserState = StorageWrapper.getUserState()
            if(cachedUserState == undefined) return false

            this.update(cachedUserState as any as UserStateData)

            return true
        } catch{
            return false
        }
    }

    private static async initFetchLoop(): Promise<boolean> {
        const maxFetchAttempts = 10
        for(let i = 0; i < maxFetchAttempts; i++) {
            const success = await this._fetchUserState()
            if(success) return true

            await delay(100)
        }

        return false
    }
}
